import { STORAGE_KEYS } from '../constants';

/**
 * Save data to localStorage
 * @param {string} key - Storage key
 * @param {any} data - Data to save
 */
export const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Get data from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Retrieved data or default value
 */
export const getFromStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Remove data from localStorage
 * @param {string} key - Storage key
 */
export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

/**
 * Clear all app data from localStorage
 */
export const clearAllStorage = () => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// User data specific functions
export const userStorage = {
  /**
   * Save user data
   * @param {object} userData - User data to save
   */
  save: (userData) => {
    saveToStorage(STORAGE_KEYS.USER_DATA, userData);
  },

  /**
   * Get user data
   * @returns {object|null} User data or null
   */
  get: () => {
    return getFromStorage(STORAGE_KEYS.USER_DATA);
  },

  /**
   * Remove user data
   */
  remove: () => {
    removeFromStorage(STORAGE_KEYS.USER_DATA);
  },

  /**
   * Check if user is logged in
   * @returns {boolean} True if user is logged in
   */
  isLoggedIn: () => {
    const userData = getFromStorage(STORAGE_KEYS.USER_DATA);
    return userData && userData.UserID;
  }
};

// Shipments data specific functions
export const shipmentsStorage = {
  /**
   * Save shipments data
   * @param {Array} shipments - Shipments array to save
   */
  save: (shipments) => {
    saveToStorage(STORAGE_KEYS.SHIPMENTS, shipments);
  },

  /**
   * Get shipments data
   * @returns {Array} Shipments array or empty array
   */
  get: () => {
    return getFromStorage(STORAGE_KEYS.SHIPMENTS, []);
  },

  /**
   * Remove shipments data
   */
  remove: () => {
    removeFromStorage(STORAGE_KEYS.SHIPMENTS);
  },

  /**
   * Add or update a shipment
   * @param {object} shipment - Shipment to add/update
   */
  addOrUpdate: (shipment) => {
    const shipments = shipmentsStorage.get();
    const existingIndex = shipments.findIndex(s => s.idChuyenXe === shipment.idChuyenXe);
    
    if (existingIndex >= 0) {
      shipments[existingIndex] = shipment;
    } else {
      shipments.push(shipment);
    }
    
    shipmentsStorage.save(shipments);
  }
};

// Settings specific functions
export const settingsStorage = {
  /**
   * Save settings
   * @param {object} settings - Settings object to save
   */
  save: (settings) => {
    saveToStorage(STORAGE_KEYS.SETTINGS, settings);
  },

  /**
   * Get settings
   * @returns {object} Settings object or default settings
   */
  get: () => {
    return getFromStorage(STORAGE_KEYS.SETTINGS, {
      theme: 'light',
      language: 'vi',
      notifications: true
    });
  },

  /**
   * Update specific setting
   * @param {string} key - Setting key
   * @param {any} value - Setting value
   */
  update: (key, value) => {
    const settings = settingsStorage.get();
    settings[key] = value;
    settingsStorage.save(settings);
  },

  /**
   * Remove settings
   */
  remove: () => {
    removeFromStorage(STORAGE_KEYS.SETTINGS);
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
export const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get storage usage information
 * @returns {object} Storage usage info
 */
export const getStorageInfo = () => {
  if (!isStorageAvailable()) {
    return { available: false };
  }

  try {
    let totalSize = 0;
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      totalSize += localStorage.getItem(key).length;
    });

    return {
      available: true,
      totalKeys: keys.length,
      totalSize: totalSize,
      totalSizeKB: Math.round(totalSize / 1024 * 100) / 100
    };
  } catch (error) {
    return { available: false, error: error.message };
  }
};
